<template lang="pug">
#reservation-schedules
  .box
    .box-body
      b-tabs(v-model="tabIndex")
        // room inout schedules
        b-tab.tab(v-if="_showReservationRegisterTab")
          template(#title)
            .icon-facility.mr-2(title="代理予約")
            span 代理予約
          ReservationRegisterTab
        // reservation schedules tab
        b-tab.tab(v-if="_showReservationSchedulesTab")
          template(#title)
            .icon-offline.mr-2(title="訪問面会スケジュール")
            span 訪問面会スケジュール
          ReservationScheduleTab(:scheduleTemplates="scheduleTemplates")
        // online reservation schedules tab
        b-tab.tab(v-if="_showOnlineReservationSchedulesTab")
          template(#title)
            .icon-online.mr-2(title="オンライン面会スケジュール")
            span オンライン面会スケジュール
          OnlineReservationScheduleTab(:scheduleTemplates="scheduleTemplates")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { tabIndexesStore } from "@/store/store";

import ReservationSettingsService, { ReservationSettingsGetModel } from "@/commons/services/ReservationSettings";
import ReservationScheduleTemplatesService, { ReservationScheduleTemplateGetModel } from "@/commons/services/ReservationScheduleTemplates";

import ReservationRegisterTab from "@/pc/containers/reservations/ReservationRegisterTab.vue";
import ReservationScheduleTab from "@/pc/containers/reservations/ReservationScheduleTab.vue";
import OnlineReservationScheduleTab from "@/pc/containers/reservations/OnlineReservationScheduleTab.vue";

/**
 * ### 面会スケジュール設定、代理登録画面コンポーネント
 */
@Component({
  components: {
    ReservationRegisterTab,
    ReservationScheduleTab,
    OnlineReservationScheduleTab
  }
})
export default class ReservationSchedules extends Vue {
  /** 訪問面会設定サービス */
  @Inject("ReservationSettingsService") reservationSettingsService!: ReservationSettingsService;
  /** 面会スケジュールテンプレートサービス */
  @Inject("ReservationScheduleTemplatesService") templateService!: ReservationScheduleTemplatesService;

  /** タブインデックス(0始まり) */
  tabIndex: number = 0;

  /** 訪問面会設定 */
  visitSetting: ReservationSettingsGetModel | null = null;
  /** 面会スケジュールテンプレート */
  scheduleTemplates: Array<ReservationScheduleTemplateGetModel> = [];

  get _showReservationRegisterTab(): boolean {
    const useReserveInstead: boolean = userStore.user.permissions.reservationsFunction.useReserveInstead;
    const permittedReservation: boolean = userStore.facility.permittedFunctions.reservation;
    const permittedOnlineReservation: boolean = userStore.facility.permittedFunctions.onlineReservation;

    if ((permittedReservation || permittedOnlineReservation) && useReserveInstead) {
      return true;
    } else {
      return false;
    }
  }

  get _showReservationSchedulesTab(): boolean {
    const permittedReservation: boolean = userStore.facility.permittedFunctions.reservation;
    const useReservationSchedules: boolean = userStore.user.permissions.reservationsFunction.useReservationSchedules;
    const useSchedule: boolean = this.visitSetting ? (this.visitSetting.reservationInputType == 1 ? true : false) : false;

    if (permittedReservation && useReservationSchedules && useSchedule) {
      return true;
    } else {
      return false;
    }
  }

  get _showOnlineReservationSchedulesTab(): boolean {
    const permittedOnlineReservation: boolean = userStore.facility.permittedFunctions.onlineReservation;
    const useOnlineReservationSchedules: boolean = userStore.user.permissions.reservationsFunction.useOnlineReservationSchedules;

    if (permittedOnlineReservation && useOnlineReservationSchedules) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    const setting: ReservationSettingsGetModel = await this.getReservationSetting();
    this.visitSetting = setting;

    const scheduleTemplates: Array<ReservationScheduleTemplateGetModel> = await this.getTemplates();
    this.scheduleTemplates = scheduleTemplates;

    this.$nextTick(() => {
      this.tabIndex = tabIndexesStore.reservationSchedulesTabIndex;
      tabIndexesStore.setReservationSchedulesTabIndex({ index: 0 });
    });

    return;
  }

  /**
   * ### 訪問面会設定を取得する
   */
  public async getReservationSetting(): Promise<ReservationSettingsGetModel> {
    const response: ReservationSettingsGetModel = await this.reservationSettingsService.get(userStore);
    return response;
  }

  /**
   * ### 面会予約スケジュールテンプレートを取得する
   */
  public async getTemplates(): Promise<Array<ReservationScheduleTemplateGetModel>> {
    const response = await this.templateService.gets(userStore);
    return response;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_mixin.scss";
@import "../../../styles/pc/_colors.scss";

#reservation-schedules {
  .icon-facility {
    @include svg-icon("../../../assets/reservation/facility.svg", $font-base-color, 24px, 24px);
  }
  .icon-offline {
    @include svg-icon("../../../assets/reservation/offline.svg", $font-base-color, 24px, 24px);
  }
  .icon-online {
    @include svg-icon("../../../assets/reservation/online.svg", $font-base-color, 24px, 24px);
  }
}
</style>
